import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { differenceInMinutes, isBefore, isSameDay, addWeeks, subWeeks, addDays, startOfDay, endOfDay, parseISO, areIntervalsOverlapping } from 'date-fns'
import PropTypes from 'prop-types'
import { filter, flatten, sortBy } from 'lodash'

import { DateTimePicker } from 'components/UI'
import { Alert, Button as MerinioButton, ButtonGroup } from 'ui'

import WeekDesktop from 'Employee/schedule/WeekDesktop'
import WeekMobile from 'Employee/schedule/WeekMobile'
import { fetchShiftsToConfirm } from 'store/actions/applicationInfo'
import { createErrorNotification } from 'store/actions/notifications'
import { Format, Parse, localizedStartOfWeek, localizedEndOfWeek, durationInWords } from 'services/Dates'
import { setAttendanceType, hasAccessToThis } from 'services/Utilities'
import { openModal } from 'store/actions/modals'
import Api from 'services/Api'
import { t } from 'translations'

const MINUTES_IN_DAY = 24 * 60

const EmployeeSchedule = ({ userId, defaultDate }) => {
  const dispatch = useDispatch()

  // Listen to state changes for punch cards
  let { employeePunchCards } = useSelector(state => state.refresh)

  // Get the start date from the params
  const start = new URLSearchParams(useLocation().search).get('start')

  const parsedStart = start && Parse(start, { inFormat: 'date' })
  const parsedDefaultDate = defaultDate && Parse(defaultDate, { inFormat: 'server' })

  const [events, setEvents] = useState([])
  const [days, setDays] = useState([])
  const [baseDate, setBaseDate] = useState(parsedStart || parsedDefaultDate || new Date()) // base date is a date object contained within the current viewed week
  const [isLoading, setIsLoading] = useState()

  const goNextWeek = () => setBaseDate(addWeeks(baseDate, 1))
  const goPreviousWeek = () => setBaseDate(subWeeks(baseDate, 1))
  const goThisWeek = () => setBaseDate(new Date())
  const goSpecificWeek = dateArray => setBaseDate(dateArray[0] || new Date())

  // Loads the events for the current week
  useEffect(() => {
    const firstDayOfWeek = localizedStartOfWeek(baseDate)
    const lastDayOfWeek = addWeeks(firstDayOfWeek, 1)

    const fetchInfo = async () => {
      setIsLoading(true)
      setEvents([])
      let attendances
      let shifts
      let timeOffs
      // Shifts that the user is allowed to mark themselves absent for
      let validAttendanceShiftMap

      // Fetch the attendances
      if (hasAccessToThis('attendanceSee')) {
        const params = {
          userId,
          rangeStart: Format(firstDayOfWeek),
          rangeEnd: Format(lastDayOfWeek),
          requestStatusIds: ['ACKNOWLEDGED', 'JUSTIFIED', 'UNJUSTIFIED', 'IN_PROGRESS'],
          returnFilesAsArray: true
        }
        const { result, success } = await Api.get('requests/leave', { params })
        if (!success) {
          dispatch(createErrorNotification())
          return setIsLoading()
        }
        attendances = result
      } else attendances = []

      if (hasAccessToThis('attendanceCreate')) {
        let { result, success } = await Api(`/schedule/attendance?userId=${userId}`)
        if (!success) validAttendanceShiftMap = []
        validAttendanceShiftMap = result.reduce((acc, { _id }) => ({ ...acc, [_id]: true }), {})
      } else validAttendanceShiftMap = []

      // Fetch the shifts
      if (hasAccessToThis('scheduleSee')) {
        const params = {
          employeeId: userId,
          rangeStart: Format(firstDayOfWeek),
          rangeEnd: Format(lastDayOfWeek),
          rangeCheckStartOnly: true,
          useNewReturn: true
        }
        const { result, success } = await Api.get('schedule/schedule', { params })
        if (!success) {
          dispatch(createErrorNotification())
          return setIsLoading()
        }
        shifts = result
      } else shifts = []

      // Fetch the timeoff
      if (hasAccessToThis('timeoffSee')) {
        const params = { userId, requestStatusId: 'APPROVED', returnFilesAsArray: true }
        const { vacations, success } = await Api.get('requests/vacation', { params })

        if (!success) {
          dispatch(createErrorNotification())
          return setIsLoading()
        }
        timeOffs = vacations
      } else timeOffs = []

      if (!Array.isArray(shifts)) {
        dispatch(createErrorNotification())
        return setIsLoading()
      }

      // Mark shifts as having been viewed
      Api.post('schedule/markAsSeen', { ids: shifts.map(({ _id }) => _id) })

      const shiftsToEvents = shifts?.length
        ? shifts.map(shift => {
            if (validAttendanceShiftMap[shift._id]) shift.canCreateAttendance = true
            return hasMatchingAttendance(shift, attendances)
          })
        : []

      const attendancesToEvents = attendances?.length
        ? attendances
            .filter(attendance => !attendance.eventId)
            .map(attendance => ({ ...setAttendanceType(attendance, attendance.requestTypeId), isAttendance: true }))
        : []

      const timeoffsToEvent = timeOffs?.length ? flatten(timeOffs.map(expandTimeOff)) : []
      // DO NOT CHANGE ORDER OF THE EVENTS IN ARRAY
      const events = [
        // Parse the attendance to add into the events array
        ...attendancesToEvents,
        // Add the time off in the shift array (also duplicate the multiday time off)
        ...timeoffsToEvent,
        ...shiftsToEvents
      ]

      setEvents(events)

      setIsLoading()
    }

    fetchInfo()
  }, [baseDate, dispatch, userId])

  useEffect(() => {
    dispatch(fetchShiftsToConfirm())
  }, [baseDate, dispatch])

  /**
   * Will expand the multi-day time off and the one spanning on 2 days
   * @param {object} timeOff the time off to parse
   */
  const expandTimeOff = timeOff => {
    timeOff.isTimeOff = true
    let baseDate = Parse(timeOff.start)
    const endDate = Parse(timeOff.end)
    const timeOffs = []

    // Expand the time off on multiple days
    while (isBefore(baseDate, endDate)) {
      const startIsTheSameDay = isSameDay(baseDate, Parse(timeOff.start))
      const start = Format(startIsTheSameDay ? baseDate : startOfDay(baseDate), { outFormat: 'server' })

      const endIsTheSameDay = isSameDay(baseDate, Parse(timeOff.end))
      const end = Format(endIsTheSameDay ? endDate : endOfDay(baseDate), { outFormat: 'server' })
      timeOffs.push({ ...timeOff, start, end })

      // If time off is on 2 day AND less then 1 day long, add one on the next day
      if (!isSameDay(Parse(timeOff.start), endDate) && differenceInMinutes(endDate, Parse(timeOff.start)) < MINUTES_IN_DAY)
        timeOffs.push({ ...timeOff, start: Format(startOfDay(addDays(baseDate, 1)), { outFormat: 'server' }), end: timeOff.end })

      // Increment the base date
      baseDate = addDays(baseDate, 1)
    }

    return timeOffs
  }

  /**
   * Add the matching attendance to the shift. This will display a border color and information about the attendance
   * (Will find the matching attendance with the shiftId, or try to match them by the start date)
   */
  const hasMatchingAttendance = (event, attendances) => {
    const { _id, isTimeOff } = event
    const linkedAttendances = []

    // Find a matching attendances request, and set it's values into the event
    const matchedById = filter(attendances, { eventId: _id })
    if (matchedById) linkedAttendances.push(...matchedById)

    // Do not match time off (only by ID)
    if (!isTimeOff) {
      // Link the attendance if it is on the same day as the event and has no event linked to it
      const matchedByDate = filter(attendances, ({ eventId, start }) => {
        const noEventLinked = !eventId
        const isOnSameDay = isSameDay(Parse(start), Parse(event.start))

        return noEventLinked && isOnSameDay
      })
      if (matchedByDate) linkedAttendances.push(...matchedByDate)
    }

    if (!linkedAttendances.length) return event

    // Sort the attendances mapped into the event (Absence first, then lateness and finaly early out)
    event.linkedAttendances = sortBy(linkedAttendances, ['requestTypeId'])

    // Adds the attendance information into the event
    event.linkedAttendances.forEach(attendance => {
      return setAttendanceType(event, attendance.requestTypeId)
    })

    return event
  }

  // Function to create an array of days of the week with events to display in WeekDesktop and WeekMobile
  useEffect(() => {
    const loadDays = async () => {
      let tempDays = []
      for (let i = 0; i < 7; i++) {
        // Create the date for each day according to the baseDate
        const date = addDays(localizedStartOfWeek(baseDate), i)
        let tempEvents = []
        let approvedTime = 0

        for (const event of events) {
          let tempEvent = { ...event }
          // If the event doesn't start on the same date, go to next event
          if (!isSameDay(date, parseISO(event.start))) continue

          // Don't load punch card if user can't see them, or is the event is an attendance or timeOff
          if ((hasAccessToThis('punchOnShifts') || hasAccessToThis('seeOwnPunches')) && !event.isAttendance && !event.isTimeOff) {
            // Load the punch card related to the shift
            const { success, result } = await Api.get(`punch/shifts/${event._id}`, { params: { dontCreateCard: true } })
            if (!success) return dispatch(createErrorNotification())
            // Add the punchcard related to the event to the event object
            tempEvent.punchCard = result

            if (result?.status === 'APPROVED') {
              // Add the sum of paid hours if the punch card is approved
              approvedTime += result.minutes.paid
              if (result?.transfers.length) {
                for (let transfer of result.transfers) {
                  approvedTime += transfer.minutes
                }
              }
            }
          }

          const shiftTimeoff = tempEvents.find(tempEvent => {
            return (
              tempEvent.isTimeOff &&
              areIntervalsOverlapping(
                { start: parseISO(event.start), end: parseISO(event.end) },
                { start: parseISO(tempEvent.start), end: parseISO(tempEvent.end) }
              )
            )
          })

          if (shiftTimeoff && !event.isTimeOff) {
            tempEvents.push({ ...tempEvent, hasTimeOff: true, vacationTypeId: shiftTimeoff.vacationTypeId, shiftTimeOffId: shiftTimeoff._id })
          } else {
            tempEvents.push(tempEvent)
          }
        }

        tempDays.push({ date, events: sortBy(tempEvents, ['start']), approvedTime })
      }
      setDays(tempDays)
    }
    loadDays()
  }, [dispatch, baseDate, events, employeePunchCards])
  //calculate the total paidHours for the week
  const weekPaidHours = days.reduce((acc, day) => acc + day.approvedTime, 0)

  const hasAccessToPunches = !!hasAccessToThis('punchOnShifts') || !!hasAccessToThis('seeOwnPunches')

  return (
    <>
      <div className="d-flex justify-content-between flex-md-row flex-column">
        <div className="d-flex flex-md-row flex-column">
          <ButtonGroup className="d-flex d-xl-block mb-1 mb-md-0 mr-0 mr-md-2">
            <MerinioButton color="secondary" outlined onClick={goPreviousWeek}>
              <i className="fas fa-caret-left" />
            </MerinioButton>
            <MerinioButton color="secondary" outlined onClick={goThisWeek}>
              {t('employee.mySchedule.THIS_WEEK')}
            </MerinioButton>
            <MerinioButton color="secondary" outlined onClick={goNextWeek}>
              <i className="fas fa-caret-right" />
            </MerinioButton>
          </ButtonGroup>
          <DateTimePicker value={baseDate} onChange={goSpecificWeek} />
        </div>
        <MerinioButton
          color="secondary"
          size="small"
          className="mt-1"
          onClick={() =>
            dispatch(openModal('ScheduleExportPDFModal', { range: [localizedStartOfWeek(baseDate), localizedEndOfWeek(baseDate)], employeeId: userId }))
          }
        >
          <i className="fas fa-file-pdf" />
          <span className="ml-1 d-md-inline">{t(`EXPORT_PDF`)}</span>
        </MerinioButton>
      </div>
      {/* Render the total approved time for the week if there is, hide the alert if the employee cannot see their own punches*/}
      <Alert variant="info" className="mt-3" show={!!hasAccessToPunches && !!weekPaidHours}>
        {t('employee.punch.APPROVED_TIME_WEEK', { weekPaidHours: durationInWords(weekPaidHours) })}
      </Alert>
      {/* Show this on desktop */}
      <div className="d-none d-xl-block mt-3">
        <WeekDesktop days={days} isLoading={isLoading} />
      </div>
      {/* Show this on mobile */}
      <div className="d-xl-none mt-3">
        <WeekMobile days={days} isLoading={isLoading} />
      </div>
    </>
  )
}

export default EmployeeSchedule

EmployeeSchedule.propTypes = {
  userId: PropTypes.string.isRequired
}
